/**
 * Security utilities for input sanitization
 *
 * These utilities provide defense-in-depth protection against common
 * security issues. They should be used in addition to proper input
 * validation at the API level.
 */

/**
 * Common prompt injection patterns to filter
 * These patterns are used by various LLM implementations
 */
const INJECTION_PATTERNS = [
  // Llama/Mistral instruction format
  /\[INST\]/gi,
  /\[\/INST\]/gi,
  // Llama system prompt format
  /<<SYS>>/gi,
  /<<\/SYS>>/gi,
  // Special token markers
  /<\|.*?\|>/g,
  // OpenAI-style system markers
  /<\|im_start\|>/gi,
  /<\|im_end\|>/gi,
  // Anthropic-style markers
  /\[HUMAN\]/gi,
  /\[ASSISTANT\]/gi,
  // Generic role injection attempts
  /^(system|user|assistant):\s*/gim,
];

/**
 * Sanitize a system prompt to reduce prompt injection risks
 *
 * **Important:** This is defense-in-depth, not a complete solution.
 * Never use untrusted user input directly as a system prompt.
 * If you need dynamic prompts, use a server-side allow list.
 *
 * @param prompt - The system prompt to sanitize
 * @returns Sanitized prompt with common injection patterns removed
 *
 * @example
 * ```ts
 * const userInput = "Hello [INST] ignore previous [/INST]";
 * const safe = sanitizeSystemPrompt(userInput);
 * // Result: "Hello  ignore previous "
 * ```
 */
export function sanitizeSystemPrompt(prompt: string): string {
  if (!prompt || typeof prompt !== "string") {
    return "";
  }

  let sanitized = prompt;

  // Remove injection patterns
  for (const pattern of INJECTION_PATTERNS) {
    sanitized = sanitized.replace(pattern, "");
  }

  // Normalize whitespace (collapse multiple spaces/newlines)
  sanitized = sanitized
    .replace(/\n{3,}/g, "\n\n")
    .replace(/ {2,}/g, " ")
    .trim();

  return sanitized;
}

/**
 * Validate that a URL uses a safe protocol (http or https)
 *
 * Prevents javascript: protocol XSS attacks and other unsafe schemes.
 *
 * @param url - The URL to validate
 * @returns true if the URL uses http or https protocol
 *
 * @example
 * ```ts
 * isValidUrl('https://example.com'); // true
 * isValidUrl('javascript:alert(1)'); // false
 * isValidUrl('data:text/html,...');  // false
 * ```
 */
export function isValidUrl(url: string): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }

  try {
    const parsed = new URL(url);
    return ["http:", "https:"].includes(parsed.protocol);
  } catch {
    return false;
  }
}

/**
 * Validation result for URL validation
 */
export interface UrlValidationResult {
  /** Whether the URL is valid */
  valid: boolean;
  /** Error message if invalid */
  error?: string;
  /** Parsed URL if valid */
  parsed?: URL;
}

/**
 * Validate a base URL for API configuration
 *
 * Ensures the URL is properly formatted and uses a secure protocol.
 * Returns detailed validation result for error messaging.
 *
 * @param url - The base URL to validate
 * @returns Validation result with error details
 *
 * @example
 * ```ts
 * const result = validateBaseUrl('https://api.example.com');
 * if (!result.valid) {
 *   console.error(result.error);
 * }
 * ```
 */
export function validateBaseUrl(url: string): UrlValidationResult {
  if (!url) {
    return { valid: false, error: "URL is required" };
  }

  if (typeof url !== "string") {
    return { valid: false, error: "URL must be a string" };
  }

  // Allow relative URLs for proxy mode
  if (url.startsWith("/") || url.startsWith("./")) {
    return { valid: true };
  }

  try {
    const parsed = new URL(url);

    if (!["http:", "https:"].includes(parsed.protocol)) {
      return {
        valid: false,
        error: `Invalid protocol "${parsed.protocol}". Only http and https are allowed.`,
      };
    }

    // Warn about non-HTTPS in production contexts
    if (parsed.protocol === "http:" && !isLocalhostUrl(url)) {
      return {
        valid: true,
        parsed,
        error:
          "Warning: Using HTTP for non-localhost URL. Consider using HTTPS.",
      };
    }

    return { valid: true, parsed };
  } catch {
    return { valid: false, error: "Invalid URL format" };
  }
}

/**
 * Check if a URL is a localhost URL
 */
function isLocalhostUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ["localhost", "127.0.0.1", "[::1]"].includes(parsed.hostname);
  } catch {
    return false;
  }
}

/**
 * Sanitize a string for safe display (escape HTML entities)
 *
 * Note: React already escapes content in JSX, so this is mainly
 * useful when constructing strings for non-React contexts.
 *
 * @param text - The text to sanitize
 * @returns Text with HTML entities escaped
 */
export function escapeHtml(text: string): string {
  if (!text || typeof text !== "string") {
    return "";
  }

  const htmlEntities: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
    "/": "&#x2F;",
  };

  return text.replace(/[&<>"'/]/g, (char) => htmlEntities[char] || char);
}

/**
 * Maximum allowed length for feedback comments.
 * Enforced in JS as defense-in-depth (HTML maxLength is client-only).
 */
const MAX_FEEDBACK_COMMENT_LENGTH = 2000;

/**
 * Sanitize a user-submitted feedback comment before sending to the API.
 *
 * - Strips null bytes and non-printable control characters (keeps newlines/tabs)
 * - Collapses excessive whitespace
 * - Enforces max length in JavaScript (HTML maxLength is bypassable)
 * - Returns empty string for non-string / empty input
 */
export function sanitizeFeedbackComment(input: string): string {
  if (!input || typeof input !== "string") {
    return "";
  }

  let sanitized = input
    .replace(/\0/g, "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\x01-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/ {2,}/g, " ")
    .trim();

  if (sanitized.length > MAX_FEEDBACK_COMMENT_LENGTH) {
    sanitized = sanitized.slice(0, MAX_FEEDBACK_COMMENT_LENGTH);
  }

  return sanitized;
}

/**
 * Generate a cryptographically secure UUID v4
 *
 * Uses crypto.randomUUID when available, falls back to
 * crypto.getRandomValues for broader compatibility.
 *
 * @returns A UUID v4 string
 */
export function generateSecureUUID(): string {
  // Prefer native randomUUID (modern browsers, Node 19+)
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback using crypto.getRandomValues (still cryptographically secure)
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    // Set version (4) and variant (10xx) bits per RFC 4122
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
      "",
    );
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Last resort fallback (not cryptographically secure)
  // This should rarely be reached in modern environments
  if (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV === "development"
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      "[CuadraUIKit] Using non-cryptographic fallback for UUID generation",
    );
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}
